import {
  Car,
  CheckCircle,
  CreditCard,
  FileText,
  HelpCircle,
  LayoutDashboard,
  Leaf,
  LogOut,
  Mail,
  Menu,
  Receipt,
  Settings,
  Shield,
  Users,
  Wind,
  X
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/index';
import { queryClient } from '../App';
import { cn, getInitials } from '../lib/utils';
import { useAuthStore } from '../store/auth';
import { Button } from './Button';

const navLinks: Array<{
  icon?: React.ComponentType<any>;
  label?: string;
  href?: string;
  adminOnly?: boolean;
  divider?: boolean;
}> = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: Users, label: 'Customers', href: '/customers' },
  { icon: Car, label: 'Vehicles', href: '/vehicles' },
  { icon: FileText, label: 'Licences', href: '/licences' },
  { icon: Shield, label: 'Insurances', href: '/insurances' },
  { icon: Wind, label: 'PUCs', href: '/pucs' },
  { icon: CheckCircle, label: 'Fitness', href: '/fitness' },
  { icon: Receipt, label: 'Road Tax', href: '/road-taxes' },
  { icon: Leaf, label: 'Env Tax', href: '/env-taxes' },
  { icon: CreditCard, label: 'Payments', href: '/payments' },
  { divider: true },
  // { icon: UserPlus, label: 'Team', href: '/team', adminOnly: true },
  // { icon: Gift, label: 'Referral', href: '/referral' },
  { icon: Settings, label: 'Settings', href: '/settings' },
  { icon: HelpCircle, label: 'Help & Support', href: '/help' },
  { icon: Mail, label: 'Contact Us', href: '/contact-us' },
]

export function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, clear } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)

  const handleLogout = async () => {
    try {
      // Logout and clear auth cookies (refresh token is HttpOnly, backend will read from cookie)
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
    // Clear auth state first
    clear()
    // Clear all cached queries
    queryClient.clear()
    // Close sidebar if open
    setIsOpen(false)
    toast.success('Logged out successfully')
    // Navigate to login
    navigate('/login', { replace: true })
  }

  const sidebarContent = (
    <>
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <img src="/image.png" alt="RTO Logo" className="w-20 h-20 rounded-lg object-cover" />
          <div>
            <h2 className="font-bold text-gray-900">RTO Agents</h2>
            <p className="text-xs text-gray-600">Agency Hub</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {navLinks.map((link, idx) => {
          if (link.divider) {
            return (
              <div key={`divider-${idx}`} className="my-4 border-t border-gray-200" />
            )
          }

          if (link.adminOnly && user?.role !== 'admin') {
            return null
          }

          const Icon = link.icon!
          const isActive = location.pathname === link.href
          return (
            <button
              key={link.href}
              onClick={() => {
                navigate(link.href!)
                setIsOpen(false)
              }}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors',
                isActive
                  ? 'bg-saffron/10 text-saffron border-l-4 border-saffron'
                  : 'text-gray-700 hover:bg-gray-100',
              )}
            >
              <Icon className="w-5 h-5" />
              <span>{link.label}</span>
            </button>
          )
        })}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-4 px-2">
          <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-sm font-bold">
            {getInitials(user?.full_name || 'U')}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-gray-600">{user?.role}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full justify-start"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </>
  )

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-col w-60 bg-white border-r border-gray-200">
        {sidebarContent}
      </div>

      {/* Mobile menu button */}
      <div className="md:hidden fixed top-4 left-4 z-40">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile sidebar */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setIsOpen(false)}>
          <div
            className="absolute left-0 top-0 bottom-0 w-60 bg-white border-r border-gray-200 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  )
}
